import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${window.__BACKEND_URL__}/api`;
const PINS_KEY = 'ht_nav_pins';
const GROUPS_KEY = 'ht_nav_groups';
const LANDING_KEY = 'ht_nav_landing';
const readJson = (k, fallback) => { try { const v = JSON.parse(localStorage.getItem(k)); return v ?? fallback; } catch { return fallback; } };

// Nav preferences (Sep 2026): localStorage paints instantly, the server copy
// (users.nav_prefs) is authoritative and follows the user to every device.
export default function useNavPrefs(token) {
  const [pins, setPinsState] = useState(() => readJson(PINS_KEY, []));
  const [groups, setGroupsState] = useState(() => readJson(GROUPS_KEY, {}));
  const [landing, setLandingState] = useState(() => localStorage.getItem(LANDING_KEY) || 'clock-in');
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);
  const pending = useRef({});
  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    axios.get(`${API}/me/nav-prefs`, auth()).then((r) => {
      if (!alive) return;
      const p = r.data || {};
      setPinsState(p.pins || []); localStorage.setItem(PINS_KEY, JSON.stringify(p.pins || []));
      setGroupsState(p.groups || {}); localStorage.setItem(GROUPS_KEY, JSON.stringify(p.groups || {}));
      setLandingState(p.landing || 'clock-in'); localStorage.setItem(LANDING_KEY, p.landing || 'clock-in');
      setLoaded(true);
    }).catch(() => setLoaded(true));
    return () => { alive = false; };
  }, [token, auth]);

  const flush = useCallback(() => {
    if (!token || !Object.keys(pending.current).length) return;
    const body = pending.current; pending.current = {};
    axios.put(`${API}/me/nav-prefs`, body, auth()).catch(() => {});
  }, [token, auth]);
  const queue = useCallback((patch) => {
    pending.current = { ...pending.current, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 600);
  }, [flush]);

  const setPins = useCallback((next) => { setPinsState(next); localStorage.setItem(PINS_KEY, JSON.stringify(next)); queue({ pins: next }); }, [queue]);
  const setGroups = useCallback((next) => { setGroupsState(next); localStorage.setItem(GROUPS_KEY, JSON.stringify(next)); queue({ groups: next }); }, [queue]);
  const setLanding = useCallback((next) => { setLandingState(next); localStorage.setItem(LANDING_KEY, next); return axios.put(`${API}/me/nav-prefs`, { landing: next }, auth()); }, [auth]);

  return { pins, setPins, groups, setGroups, landing, setLanding, loaded };
}
